/** Web Speech helpers for hands-free salon capture. */

export function getSpeechRecognition() {
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export function canUseVoice() {
  const hasListen = Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  const hasSpeak = typeof window.speechSynthesis !== 'undefined';
  return hasListen && hasSpeak;
}

export function voiceUnsupportedReason() {
  if (typeof window === 'undefined') return 'Voice unavailable.';
  if (!window.isSecureContext && window.location?.hostname !== 'localhost') {
    return 'Microphone needs HTTPS (or localhost).';
  }
  if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
    return 'Voice commands need Chrome or Edge on this device.';
  }
  if (typeof window.speechSynthesis === 'undefined') {
    return 'Speech reply is not supported on this browser.';
  }
  return '';
}

function pickEnglishVoice(voices) {
  if (!voices?.length) return null;
  const en = voices.filter((v) => v.lang?.startsWith('en'));
  const pool = en.length ? en : voices;
  const local = pool.find((v) => v.localService);
  return local || pool[0];
}

/** Load voices — Chrome returns [] until first user gesture + voiceschanged. */
export function ensureSpeechVoices(timeoutMs = 2500) {
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    return Promise.resolve([]);
  }
  const existing = window.speechSynthesis.getVoices();
  if (existing.length) return Promise.resolve(existing);
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve(window.speechSynthesis.getVoices());
    };
    const timer = setTimeout(finish, timeoutMs);
    window.speechSynthesis.onvoiceschanged = () => {
      clearTimeout(timer);
      window.speechSynthesis.onvoiceschanged = null;
      finish();
    };
    window.speechSynthesis.getVoices();
  });
}

/**
 * Call synchronously from a click/touch handler so Chrome allows TTS + STT.
 */
export function unlockSpeechSynthesis() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(' ');
    utter.volume = 0.01;
    utter.rate = 1;
    utter.lang = 'en-IN';
    window.speechSynthesis.speak(utter);
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }
  } catch {
    /* optional */
  }
}

/**
 * Request mic permission then release the stream.
 * Chrome cannot run SpeechRecognition while getUserMedia audio is open.
 */
export async function primeMicrophoneForRecognition() {
  if (typeof window === 'undefined') {
    throw new Error('Microphone unavailable.');
  }
  if (!window.isSecureContext && window.location?.hostname !== 'localhost') {
    throw new Error('Microphone needs HTTPS. Open the salon link over https.');
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser cannot access the microphone.');
  }
  let stream = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
      },
      video: false,
    });
  } catch (err) {
    const name = err?.name || '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      throw new Error(
        'Microphone blocked. Allow mic for this site in browser settings (lock icon in the address bar), then tap Mic again.'
      );
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      throw new Error('No microphone found on this device.');
    }
    if (name === 'SecurityError') {
      throw new Error(
        'Microphone is blocked by browser or site policy. Use HTTPS and allow microphone for this site.'
      );
    }
    throw new Error(err?.message || 'Could not access microphone.');
  } finally {
    stream?.getTracks?.().forEach((track) => track.stop());
  }
}

/** @deprecated use primeMicrophoneForRecognition */
export async function requestMicrophoneAccess() {
  return primeMicrophoneForRecognition();
}

export async function speak(text, { lang = 'en-IN' } = {}) {
  if (typeof window.speechSynthesis === 'undefined') {
    return false;
  }
  const spoken = String(text || '').trim();
  if (!spoken) return false;

  const voices = await ensureSpeechVoices();
  return new Promise((resolve) => {
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(spoken);
      utter.lang = lang;
      utter.rate = 1;
      utter.pitch = 1;
      utter.volume = 1;
      const voice = pickEnglishVoice(voices);
      if (voice) utter.voice = voice;
      utter.onend = () => resolve(true);
      utter.onerror = () => resolve(false);
      window.speechSynthesis.speak(utter);
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
    } catch {
      resolve(false);
    }
  });
}

export function speechRecognitionErrorMessage(errorCode) {
  switch (errorCode) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission needed for voice commands. Allow mic and tap Mic again.';
    case 'audio-capture':
      return 'Could not capture audio. Close other apps using the mic and tap Mic again.';
    case 'network':
      return 'Voice commands need internet (Chrome uses Google speech). Check your connection.';
    case 'no-speech':
      return '';
    case 'aborted':
      return '';
    default:
      return errorCode ? `Voice error: ${errorCode}` : '';
  }
}

/**
 * Map transcript → capture action.
 * @returns {'capture'|'add'|'next'|null}
 */
export function parseVoiceCommand(transcript) {
  const raw = String(transcript || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!raw) return null;

  if (
    /\b(next|done|finish|finish up|upload|continue|proceed|move on)\b/.test(raw)
  ) {
    return 'next';
  }
  if (
    /\b(add|add cut|add another|another cut|one more|more cut)\b/.test(raw)
  ) {
    return 'add';
  }
  if (
    /\b(capture|start|record|retake|shoot|begin|go|action)\b/.test(raw)
  ) {
    return 'capture';
  }
  return null;
}
